import React, { useEffect, useState } from 'react';
import './App.css';
import VoiceAgentSettings from './components/VoiceAgentSettings';
import Login from './components/Login';
import AdminLogin from './components/AdminLogin';
import Registration from './components/Registration';
import MarketingPage from './components/MarketingPage';
import LegalTerms from './components/LegalTerms';
import LegalPrivacy from './components/LegalPrivacy';
import ListenPage from './components/ListenPage';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import Sidebar from './components/Sidebar';
import EmailVerification from './components/EmailVerification';
import OnboardingFlow from './components/OnboardingFlow';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getMarketingUrl } from './utils/urlHelper';

/** Only used on production app subdomain — never redirect localhost to production. */
function RedirectToMarketingListen() {
  useEffect(() => {
    window.location.replace(getMarketingUrl('/listen'));
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-gray-400 text-sm">
      Opening listen page…
    </div>
  );
}

/**
 * On app.appifyai.com, /listen is served from marketing hosting — redirect there.
 * On localhost (any port, e.g. 3004), render ListenPage so you can iterate without deploying.
 */
function ListenRouteWhenAppShell() {
  const host = window.location.hostname;
  if (host === 'app.appifyai.com') {
    return <RedirectToMarketingListen />;
  }
  return (
    <div className="fixed inset-0 z-[30] overflow-y-auto">
      <ListenPage />
    </div>
  );
}

export const CLIENT_AGENT_MAP = {
  neil: { id: 'acb59b5f-4648-4d63-b5bf-2595998b532a', password: 'neilpass' },
  bernie: { id: 'acb59b5f-4648-4d63-b5bf-2595998b532a', password: 'berniepass' },
};

function AppContent() {
  const [toast, setToast] = useState('');
  const [darkMode] = useState(() => localStorage.getItem('darkMode') === 'true' || false);
  const { isAuthenticated, isAuthenticatedButUnverified, user } = useAuth();

  // CRITICAL: appifyai.com / www must ALWAYS be marketing. Do not use REACT_APP_APP_SHELL alone for
  // production — if APP_SHELL=1 is baked in from .env.local during `npm run build`, every host would
  // show register. App shell only on app subdomain, or localhost when developing the app.
  //
  // Local marketing preview: set REACT_APP_MARKETING_PREVIEW=1 when starting `npm start` (e.g. PORT=3004)
  // so / and /listen use MarketingPage + ListenPage. If you use APP_SHELL=1 only, /listen still renders
  // ListenPage on localhost without redirecting to production.
  const host = window.location.hostname;
  const isLocalHost =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host === '[::1]';
  const forceMarketingOnLocal =
    isLocalHost && process.env.REACT_APP_MARKETING_PREVIEW === '1';
  const isLocalAppDev =
    !forceMarketingOnLocal &&
    isLocalHost &&
    process.env.REACT_APP_APP_SHELL === '1';
  const isAppDomain = host === 'app.appifyai.com' || isLocalAppDev;

  const urlParams = new URLSearchParams(window.location.search);
  const selectedPlan = urlParams.get('plan');

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleLogin = () => showToast('Welcome back!');
  const handleRegister = (userData) => {
    if (userData?.user) {
      showToast('Welcome back!');
      window.location.href = '/onboarding';
    } else {
      showToast(`Welcome ${userData?.firstName || userData?.displayName || ''}!`);
    }
  };

  if (isAppDomain) {
    return (
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="w-full h-full min-h-screen flex relative" style={{ backgroundColor: '#07070f' }}>
          {/* Subtle purple ambient glow — auth/onboarding pages */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)', filter: 'blur(80px)' }} />
          </div>
          {/* Global subtle grid */}
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.025) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
          <div className="relative z-10 w-full flex-1 min-h-screen flex items-center justify-center">
            <Routes>
              <Route path="/" element={<Navigate to="/register" replace />} />
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/admin" element={<AdminLogin onLogin={handleLogin} />} />
              <Route
                path="/register"
                element={<Registration onRegister={handleRegister} selectedPlan={selectedPlan} />}
              />
              <Route
                path="/verify-email"
                element={
                  isAuthenticatedButUnverified ? (
                    <EmailVerification
                      userEmail={user?.email}
                      onVerified={() => console.log('verified')}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/onboarding"
                element={
                  isAuthenticated ? (
                    <OnboardingFlow onComplete={() => (window.location.href = '/dashboard')} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/app"
                element={
                  isAuthenticated ? (
                    <div className="flex h-screen w-full" style={{ backgroundColor: '#07070f' }}>
                      <Sidebar />
                      <main className="flex-1 min-w-0 overflow-auto pb-20 md:pb-0" style={{ backgroundColor: '#07070f' }}>
                        <VoiceAgentSettings showToast={showToast} />
                      </main>
                    </div>
                  ) : isAuthenticatedButUnverified ? (
                    <Navigate to="/verify-email" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  isAuthenticated ? (
                    <div className="flex h-screen w-full">
                      <Sidebar />
                      <main className="flex-1 min-w-0 overflow-auto pb-20 md:pb-0">
                        <Dashboard />
                      </main>
                    </div>
                  ) : isAuthenticatedButUnverified ? (
                    <Navigate to="/verify-email" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/settings"
                element={
                  isAuthenticated ? (
                    <div className="flex h-screen w-full">
                      <Sidebar />
                      <main className="flex-1 min-w-0 overflow-auto pb-20 md:pb-0">
                        <Settings />
                      </main>
                    </div>
                  ) : isAuthenticatedButUnverified ? (
                    <Navigate to="/verify-email" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route path="/listen" element={<ListenRouteWhenAppShell />} />
              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      isAuthenticated
                        ? '/dashboard'
                        : isAuthenticatedButUnverified
                          ? '/verify-email'
                          : '/register'
                    }
                    replace
                  />
                }
              />
            </Routes>
          </div>
          {toast && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 text-white px-6 py-3 rounded-xl shadow-lg z-50 text-sm font-medium" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)', backdropFilter: 'blur(12px)' }}>
              {toast}
            </div>
          )}
        </div>
      </Router>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="w-full h-full min-h-screen flex flex-col transition-colors duration-500">
        <Routes>
          <Route path="/" element={<MarketingPage />} />
          <Route path="/terms" element={<LegalTerms />} />
          <Route path="/privacy" element={<LegalPrivacy />} />
          <Route
            path="/listen"
            element={
              <div className="flex min-h-screen w-full flex-1 flex-col">
                <ListenPage />
              </div>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {toast && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-xl shadow-lg z-50">
            {toast}
          </div>
        )}
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
